import { TILE_SIZE } from '@/sprites/tiles';
import { Tilemap } from '@/world/tilemap';

/**
 * A named place, with the doors that lead out of it.
 *
 * Areas are declared as data so adding a new one is a file in `src/data/maps`
 * plus an entry in the registry — no code changes. Portals are resolved by tile
 * coordinate rather than by walking into an edge, which keeps a door a door
 * even when the map around it is redrawn.
 */

export interface Portal {
  /** Tile that triggers the transition. */
  readonly x: number;
  readonly y: number;
  // Authored only. Stairs into a dungeon are tiles, because a portal carries
  // one fixed destination and "one floor further down" is not one.
  readonly toArea: AuthoredAreaId;
  /** Tile the player arrives on. */
  readonly toX: number;
  readonly toY: number;
}

export interface AreaDefinition {
  readonly id: AreaId;
  readonly name: string;
  readonly rows: readonly string[];
  /** Where a fresh game, or a broken save, puts the player. */
  readonly spawn: { readonly x: number; readonly y: number };
  readonly portals: readonly Portal[];
  /** Interiors ignore weather and are only dimly affected by the hour. */
  readonly indoor?: boolean;
  /**
   * Whether crops live here.
   *
   * There is one plot layer and it belongs to the farm. Without this the
   * layer was drawn in every area at the same tile coordinates, so a planted
   * field appeared underground on every floor of the dungeon — and tilling
   * the grass at the coast quietly edited the farm.
   */
  readonly farmable?: boolean;
  /** Tile the player can sleep on, if any. */
  readonly bed?: { readonly x: number; readonly y: number };
  /** Fenced area animals stay inside, in tiles. */
  readonly pen?: {
    readonly minX: number;
    readonly minY: number;
    readonly maxX: number;
    readonly maxY: number;
  };
  /** Where the feed trough is drawn. */
  readonly trough?: { readonly x: number; readonly y: number };
  /** Furniture a new game finds already in the room. */
  readonly furniture?: readonly { readonly id: string; readonly x: number; readonly y: number }[];
}

/**
 * Every place, authored or generated.
 *
 * `dungeon` has no entry in the registry: floors are built per descent by
 * `makeDungeonArea`, because there is a different one each time and caching
 * them would both grow without limit and pin layouts meant to be regenerated.
 */
export type AreaId = AuthoredAreaId | 'dungeon';

/** The places that exist as files in `src/data/maps`. */
export type AuthoredAreaId = 'farm' | 'cottage' | 'coast';

export class Area {
  readonly id: AreaId;
  readonly name: string;
  readonly map: Tilemap;
  readonly indoor: boolean;
  private readonly portals = new Map<string, Portal>();
  readonly definition: AreaDefinition;

  constructor(definition: AreaDefinition) {
    this.definition = definition;
    this.id = definition.id;
    this.name = definition.name;
    this.map = new Tilemap(definition.rows);
    this.indoor = definition.indoor ?? false;

    for (const portal of definition.portals) {
      if (portal.x < 0 || portal.y < 0 || portal.x >= this.map.width || portal.y >= this.map.height) {
        throw new Error(`Area "${definition.id}" has a portal outside the map at ${portal.x},${portal.y}`);
      }
      if (this.map.isSolidTile(portal.x, portal.y)) {
        throw new Error(
          `Area "${definition.id}" has a portal on a solid tile at ${portal.x},${portal.y}`,
        );
      }
      this.portals.set(key(portal.x, portal.y), portal);
    }
  }

  portalAt(tileX: number, tileY: number): Portal | undefined {
    return this.portals.get(key(tileX, tileY));
  }

  get bounds(): { width: number; height: number } {
    return { width: this.map.pixelWidth, height: this.map.pixelHeight };
  }

  /** Sprite top-left in pixels for a given tile, so the player lands centred. */
  static tileToPixel(tileX: number, tileY: number): { x: number; y: number } {
    return { x: tileX * TILE_SIZE, y: tileY * TILE_SIZE };
  }
}

function key(x: number, y: number): string {
  return `${x},${y}`;
}
